import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, ArrowRight, Bookmark, Check, Clock, Copy, Download, RotateCcw, Share2, Sparkles, Ticket,
} from "lucide-react";
import api from "@/lib/api";
import { fmtDate } from "@/lib/date";
import { Seo } from "@/components/Seo";
import { useAuth } from "@/context/AuthContext";
import { useAuthModal } from "@/context/AuthModalContext";
import { visibleSteps } from "@/data/recQuestions";
import { NUDGES } from "@/data/recNudges";
import { recommend, answersToQuery, queryToAnswers } from "@/lib/recommend";
import { makeShareCard } from "@/lib/shareCard";

const FILE_NAME = "my-next-show.png";
const LETTERS = "ABCDEFGHIJ";
const RANK_LABELS = ["Best match", "Also a great fit", "Another one to try"];
const FLAG_LABELS = {
  language: "Strong language or adult jokes",
  heavy: "Upsetting themes",
  loud: "Loud noises or flashing lights",
  participation: "Audience participation",
};
const STATUS_STYLES = {
  "Now Playing": "bg-emerald-500/15 text-emerald-300 border-emerald-400/30",
  "Coming Soon": "bg-sky-500/15 text-sky-300 border-sky-400/30",
  "Closing Soon": "bg-amber-500/15 text-amber-300 border-amber-400/30",
};

const money = (n) => (Number.isInteger(n) ? `£${n}` : `£${n.toFixed(2)}`);
const duration = (mins) => `${Math.floor(mins / 60)}h${mins % 60 ? ` ${mins % 60}m` : ""}`;

function Frame({ children }) {
  return (
    <div className="relative overflow-hidden">
      <div
        className="pointer-events-none absolute inset-x-0 top-0 h-[520px]"
        aria-hidden="true"
        style={{ background: "radial-gradient(60% 70% at 50% 0%, rgba(200,155,60,0.16), transparent 70%)" }}
      />
      <div className="relative mx-auto max-w-3xl px-4 py-10 sm:px-6 sm:py-16">{children}</div>
    </div>
  );
}

function Intro({ onStart }) {
  return (
    <div className="fade-up rounded-3xl border border-[#C89B3C]/30 bg-gradient-to-br from-[#C89B3C]/15 to-[#161118]/80 p-8 text-center backdrop-blur sm:p-12">
      <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-[#C89B3C]/20 text-[#C89B3C]">
        <Ticket className="h-7 w-7" />
      </div>
      <h1 className="mt-6 font-serif text-4xl font-semibold leading-tight text-amber-50 sm:text-5xl">
        Which show should I see?
      </h1>
      <p className="mx-auto mt-4 max-w-md text-lg leading-8 text-amber-50/80">
        Answer a few quick questions about you and what you enjoy, and we will pick three West End shows to suit. We only
        suggest shows that are right for the youngest person coming.
      </p>
      <p className="mt-3 text-sm text-amber-100/60">About two minutes. No sign-in needed.</p>
      <button
        type="button"
        onClick={onStart}
        className="group mt-8 inline-flex items-center gap-2 rounded-full bg-[#C89B3C] px-8 py-3.5 text-lg font-semibold text-black transition-transform hover:scale-105"
      >
        Find my show
        <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
      </button>
    </div>
  );
}

function Step({ step, index, total, value, picked, onPick, onToggle, onContinue, onBack }) {
  const pct = Math.round((index / total) * 100);
  const answered = step.type === "multi" ? true : value !== undefined && picked === null;
  const isAge = step.type === "age";
  return (
    <div key={step.id} className="fade-up">
      <div className="flex items-center justify-between text-sm text-amber-100/70">
        <button type="button" onClick={onBack} className="inline-flex items-center gap-1.5 transition-colors hover:text-amber-50">
          <ArrowLeft className="h-4 w-4" /> Back
        </button>
        <span>
          Question {index + 1} of {total}
        </span>
      </div>
      <div className="mt-3 h-1.5 overflow-hidden rounded-full bg-white/10" role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100}>
        <div className="h-full rounded-full bg-[#C89B3C] transition-all duration-500" style={{ width: `${pct + 100 / total}%` }} />
      </div>

      <h2 className="mt-8 font-serif text-2xl font-semibold leading-snug text-amber-50 sm:text-3xl">{step.text}</h2>
      {step.note && <p className="mt-2 text-sm text-amber-100/60">{step.note}</p>}

      <div className={isAge ? "mt-6 grid grid-cols-3 gap-3 sm:grid-cols-5" : "mt-6 grid gap-3"}>
        {step.options.map((o, i) => {
          const selected =
            step.type === "multi"
              ? (value || []).includes(o.value)
              : picked === o.value || (picked === null && value === o.value);
          return (
            <button
              key={String(o.value)}
              type="button"
              aria-pressed={selected}
              onClick={() => (step.type === "multi" ? onToggle(o.value) : onPick(o.value))}
              className={`group flex w-full items-start gap-4 rounded-2xl border text-left transition-colors ${
                isAge ? "justify-center p-3 text-center" : "p-4"
              } ${
                selected
                  ? "border-[#C89B3C] bg-[#C89B3C]/15"
                  : "border-white/10 bg-[#161118]/70 hover:border-[#C89B3C]/60 hover:bg-[#C89B3C]/5"
              }`}
            >
              {!isAge && (
                <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-[#C89B3C]/15 text-sm font-semibold text-[#C89B3C]">
                  {step.type === "multi" && selected ? <Check className="h-4 w-4" /> : LETTERS[i]}
                </span>
              )}
              <span className={isAge ? "font-medium text-amber-50" : "pt-0.5 leading-7 text-amber-50/90"}>{o.label}</span>
            </button>
          );
        })}
      </div>

      {answered && (
        <button
          type="button"
          onClick={onContinue}
          className="group mt-6 flex w-full items-center justify-center gap-2 rounded-full bg-[#C89B3C] px-6 py-3.5 text-lg font-semibold text-black transition-transform hover:scale-[1.02]"
        >
          Continue
          <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
        </button>
      )}
    </div>
  );
}

function WantButton({ show }) {
  const { user, refresh } = useAuth();
  const { openAuthModal } = useAuthModal();
  const [saved, setSaved] = useState(false);
  const [busy, setBusy] = useState(false);
  const already = saved || Boolean(user && (user.want_shows || []).includes(show.id));

  const add = async () => {
    setBusy(true);
    try {
      await api.post(`/watchlist/want/${show.id}`);
      setSaved(true);
      toast.success(`${show.name} is on your Want to see list`);
      if (typeof refresh === "function") refresh();
    } catch {
      toast.error("Could not save that. Please try again.");
    } finally {
      setBusy(false);
    }
  };
  const click = () => (user ? add() : openAuthModal(add, "Sign in or create an account to save this show to your Want to see list."));

  return (
    <button
      type="button"
      onClick={click}
      disabled={already || busy}
      className="inline-flex items-center gap-2 rounded-full border border-white/20 px-5 py-2.5 text-sm font-semibold text-amber-50 transition-colors hover:bg-white/10 disabled:opacity-80"
    >
      {already ? <Check className="h-4 w-4 text-[#C89B3C]" /> : <Bookmark className="h-4 w-4" />}
      {already ? "On your Want to see list" : "Add to Want to see"}
    </button>
  );
}

function PickCard({ pick, rank }) {
  const { show, reasons, price } = pick;
  const p = show.rec_profile;
  const slug = show.slug || show.id;
  const canCompare = show.price_guide || (show.ticket_links || []).length > 1;
  const status = show.status;
  const statusText =
    status === "Coming Soon" ? (show.opening_date ? `Opens ${fmtDate(show.opening_date)}` : "Coming soon") : status;
  const flags = (p.flags || []).map((f) => FLAG_LABELS[f]).filter(Boolean);

  return (
    <article className="fade-up rounded-3xl border border-[#C89B3C]/25 bg-gradient-to-br from-[#C89B3C]/10 to-[#161118]/80 p-5 backdrop-blur sm:p-7">
      <div className="flex gap-4 sm:gap-5">
        {show.image_url && (
          <img src={show.image_url} alt="" loading="lazy" className="h-32 w-24 flex-shrink-0 rounded-xl object-cover sm:h-40 sm:w-28" />
        )}
        <div className="min-w-0 flex-1">
          <p className="text-xs font-semibold uppercase tracking-wider text-[#C89B3C]">{RANK_LABELS[rank]}</p>
          <h3 className="mt-1 font-serif text-2xl font-semibold leading-tight text-amber-50 sm:text-3xl">{show.name}</h3>
          <p className="mt-1 text-sm text-amber-100/70">
            {p.kind} at {show.venue}
          </p>
          <span
            className={`mt-2 inline-block rounded-full border px-2.5 py-0.5 text-xs font-medium ${STATUS_STYLES[status] || "border-white/20 text-amber-100/80"}`}
          >
            {statusText}
          </span>
        </div>
      </div>

      <p className="mt-4 font-serif text-lg italic leading-7 text-amber-50/90">{p.pitch}</p>
      {reasons.length > 0 && (
        <p className="mt-3 text-amber-50/85">
          <span className="font-medium text-[#C89B3C]">Why it fits you: </span>
          {reasons.join(", ")}.
        </p>
      )}

      <dl className="mt-4 grid gap-x-6 gap-y-2 text-sm sm:grid-cols-3">
        <div>
          <dt className="text-amber-100/60">Tickets</dt>
          <dd className="font-medium text-amber-50">{price !== null ? `From ${money(price)}` : "Check price"}</dd>
        </div>
        <div>
          <dt className="text-amber-100/60">Running time</dt>
          <dd className="inline-flex items-center gap-1.5 font-medium text-amber-50">
            <Clock className="h-3.5 w-3.5 text-[#C89B3C]" /> About {duration(p.mins)}
          </dd>
        </div>
        <div>
          <dt className="text-amber-100/60">Age</dt>
          <dd className="font-medium text-amber-50">{show.age_guidance ? show.age_guidance : `Recommended ${p.min_age}+`}</dd>
        </div>
      </dl>
      {flags.length > 0 && (
        <p className="mt-3 text-sm text-amber-100/70">
          <span className="text-amber-100/60">Good to know: </span>
          {flags.join(". ")}.
        </p>
      )}

      <div className="mt-5 flex flex-wrap gap-3">
        <Link
          to={`/shows/${slug}`}
          className="inline-flex items-center gap-2 rounded-full bg-[#C89B3C] px-5 py-2.5 text-sm font-semibold text-black transition-transform hover:scale-105"
        >
          See the show <ArrowRight className="h-4 w-4" />
        </Link>
        {canCompare && (
          <Link
            to={`/shows/${slug}/prices`}
            className="inline-flex items-center gap-2 rounded-full border border-[#C89B3C] px-5 py-2.5 text-sm font-semibold text-[#C89B3C] transition-colors hover:bg-[#C89B3C]/15"
          >
            <Ticket className="h-4 w-4" /> Compare ticket prices
          </Link>
        )}
        <WantButton show={show} />
      </div>
    </article>
  );
}

function SharePanel({ top }) {
  const [card, setCard] = useState(null);
  const show = top.show;

  useEffect(() => {
    let alive = true;
    let url = null;
    setCard(null);
    makeShareCard({
      role: show.name, show: "", line: show.rec_profile.pitch,
      ticketText: "My next show", lead: "I should see", byline: `at ${show.venue}`,
      footerQuestion: "Which show should you see?", urlText: "mytheatre.uk/find-a-show",
    })
      .then((blob) => {
        if (!alive) return;
        url = URL.createObjectURL(blob);
        setCard({ file: new File([blob], FILE_NAME, { type: "image/png" }), url });
      })
      .catch(() => {});
    return () => {
      alive = false;
      if (url) URL.revokeObjectURL(url);
    };
  }, [show.id, show.name, show.venue, show.rec_profile.pitch]);

  const canShareFile = Boolean(
    card && typeof navigator !== "undefined" && typeof navigator.share === "function" &&
      typeof navigator.canShare === "function" && navigator.canShare({ files: [card.file] })
  );
  const saveImage = () => {
    if (!card) return;
    const a = document.createElement("a");
    a.href = card.url;
    a.download = FILE_NAME;
    document.body.appendChild(a);
    a.click();
    a.remove();
    toast.success("Image saved. Post it to Instagram, TikTok or Snapchat.");
  };
  const shareImage = async () => {
    if (!card) return;
    try {
      await navigator.share({ files: [card.file] });
    } catch (err) {
      if (err && (err.name === "AbortError" || err.name === "InvalidStateError")) return;
      saveImage();
    }
  };
  const copyCaption = async () => {
    const text = `My next West End show is ${show.name}! Find yours at mytheatre.uk/find-a-show #westend #theatretok`;
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Caption copied. Paste it into your post.");
    } catch {
      toast.error("Could not copy that. You can type the caption yourself.");
    }
  };

  return (
    <section className="mt-10 rounded-3xl border border-white/10 bg-[#161118]/70 p-6 sm:p-8">
      <h2 className="font-serif text-2xl font-semibold text-amber-50">Share your top pick</h2>
      <div className="mt-5 flex flex-col items-center gap-6 sm:flex-row sm:items-start">
        <div className="w-40 flex-shrink-0">
          {card ? (
            <img src={card.url} alt="Your share card" className="w-full rounded-xl border border-[#C89B3C]/40 shadow-lg" />
          ) : (
            <div className="aspect-[9/16] w-full animate-pulse rounded-xl bg-white/5" />
          )}
        </div>
        <div className="w-full">
          <div className="flex flex-wrap gap-3">
            {canShareFile && (
              <button type="button" onClick={shareImage} className="inline-flex items-center gap-2 rounded-full bg-[#C89B3C] px-6 py-3 font-semibold text-black transition-transform hover:scale-105">
                <Share2 className="h-4 w-4" /> Share my pick
              </button>
            )}
            <button type="button" onClick={saveImage} disabled={!card} className="inline-flex items-center gap-2 rounded-full border border-white/20 px-6 py-3 font-semibold text-amber-50 transition-colors hover:bg-white/10 disabled:opacity-60">
              <Download className="h-4 w-4" /> Save image
            </button>
            <button type="button" onClick={copyCaption} className="inline-flex items-center gap-2 rounded-full border border-white/20 px-6 py-3 font-semibold text-amber-50 transition-colors hover:bg-white/10">
              <Copy className="h-4 w-4" /> Copy caption
            </button>
          </div>
          <p className="mt-4 text-sm leading-6 text-amber-100/70">
            {canShareFile
              ? "Share my pick opens your phone's share menu. Instagram, TikTok and Snapchat show up there if you have them installed. If one is missing, save the image and post it from the app."
              : "On a computer, save the image and post it from your phone. Open this page on your phone and you can share straight to Instagram, TikTok or Snapchat."}
          </p>
        </div>
      </div>
    </section>
  );
}

function Results({ answers, onRestart }) {
  const [shows, setShows] = useState(null);
  const [failed, setFailed] = useState(false);
  const key = JSON.stringify(answers);

  useEffect(() => {
    api
      .get("/shows")
      .then((r) => setShows(r.data || []))
      .catch(() => setFailed(true));
  }, []);

  const result = useMemo(() => (shows ? recommend(shows, answers, NUDGES) : null), [shows, key]); // eslint-disable-line react-hooks/exhaustive-deps

  if (failed) {
    return (
      <div className="rounded-3xl border border-white/10 bg-[#161118]/70 p-8 text-center">
        <p className="text-amber-50">We could not load the shows just now. Please try again in a moment.</p>
        <button type="button" onClick={onRestart} className="mt-5 rounded-full bg-[#C89B3C] px-6 py-3 font-semibold text-black">
          Start again
        </button>
      </div>
    );
  }
  if (!result) {
    return (
      <div className="space-y-4" aria-busy="true">
        {[0, 1, 2].map((i) => (
          <div key={i} className="h-56 animate-pulse rounded-3xl bg-white/5" />
        ))}
      </div>
    );
  }

  const { picks, relaxed } = result;
  const family = answers.party === "family";

  return (
    <div className="fade-up">
      <p className="text-sm text-[#C89B3C]">Your West End picks</p>
      <h1 className="mt-1 font-serif text-3xl font-semibold leading-tight text-amber-50 sm:text-5xl">
        {picks.length ? (picks.length === 1 ? "We found one show for you" : `We found ${picks.length} shows for you`) : "No shows fit all of that"}
      </h1>

      {relaxed.includes("length") && (
        <p className="mt-3 rounded-xl border border-white/10 bg-[#161118]/70 p-3 text-sm text-amber-100/80">
          Not enough shows fit your time limit, so we included some that run a little longer.
        </p>
      )}
      {relaxed.includes("budget") && (
        <p className="mt-3 rounded-xl border border-white/10 bg-[#161118]/70 p-3 text-sm text-amber-100/80">
          Not enough shows fit your budget, so these are the closest above it. Check each price before you book.
        </p>
      )}
      {picks.length > 0 && picks.length < 3 && (
        <p className="mt-3 rounded-xl border border-white/10 bg-[#161118]/70 p-3 text-sm text-amber-100/80">
          Only {picks.length === 1 ? "one show suits" : `${picks.length} shows suit`} your answers.
          {family ? " We only suggest shows made for the youngest child's age, so the list can be short for little ones." : ""}
        </p>
      )}
      {picks.length === 0 && (
        <p className="mt-3 text-amber-100/80">
          Try changing what you would rather avoid, or your budget, and we will look again.
        </p>
      )}

      <div className="mt-6 space-y-5">
        {picks.map((pick, i) => (
          <PickCard key={pick.show.id} pick={pick} rank={i} />
        ))}
      </div>

      <p className="mt-4 text-xs leading-5 text-amber-100/60">
        Always check the theatre's own age guidance and your tickets before you book. Prices are the lowest we have seen
        and change often.
      </p>

      {picks.length > 0 && <SharePanel top={picks[0]} />}

      <div className="mt-10 flex flex-col items-center gap-3 text-center">
        <button type="button" onClick={onRestart} className="inline-flex items-center gap-2 text-sm text-amber-100/70 transition-colors hover:text-amber-50">
          <RotateCcw className="h-4 w-4" /> Answer the questions again
        </button>
        <Link to="/quiz" className="inline-flex items-center gap-2 text-sm text-[#C89B3C] hover:underline">
          <Sparkles className="h-4 w-4" /> Or find out which West End role you are
        </Link>
      </div>
    </div>
  );
}

export default function Recommend() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const query = params.toString();
  const fromUrl = useMemo(() => queryToAnswers(new URLSearchParams(query)), [query]);

  const [answers, setAnswers] = useState({});
  const [idx, setIdx] = useState(-1); // -1 is the start screen
  const [picked, setPicked] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [idx, query]);

  const steps = visibleSteps(answers);

  const finish = (ans) => {
    const complete = { ...ans, youngest: ans.party === "family" ? ans.youngest : 18, avoid: ans.avoid || [] };
    navigate(`/find-a-show?${answersToQuery(complete)}`);
  };
  const advance = (i, ans) => {
    const list = visibleSteps(ans);
    if (i + 1 >= list.length) finish(ans);
    else setIdx(i + 1);
  };

  const pick = (value) => {
    if (picked !== null) return;
    const i = idx;
    const step = steps[i];
    setPicked(value);
    setTimeout(() => {
      const next = { ...answers, [step.id]: value };
      if (step.id === "party" && value !== "family") next.youngest = 18;
      if (step.id === "party" && value === "family" && next.youngest === 18) delete next.youngest;
      setAnswers(next);
      setPicked(null);
      advance(i, next);
    }, 240);
  };

  const toggle = (value) => {
    const current = answers.avoid || [];
    setAnswers({ ...answers, avoid: current.includes(value) ? current.filter((x) => x !== value) : [...current, value] });
  };

  const start = () => {
    setAnswers({});
    setIdx(0);
    if (fromUrl) navigate("/find-a-show");
  };

  const step = steps[idx];
  return (
    <>
      <Seo
        title="Which show should I see? West End show finder"
        description="Answer a few quick questions about you and what you enjoy, and we will pick three West End shows to suit, with ages, prices and booking links. Free, with no sign-in."
        path="/find-a-show"
      />
      <Frame>
        {fromUrl ? (
          <Results answers={fromUrl} onRestart={start} />
        ) : step ? (
          <Step
            key={step.id}
            step={step}
            index={idx}
            total={steps.length}
            value={answers[step.id]}
            picked={picked}
            onPick={pick}
            onToggle={toggle}
            onContinue={() => advance(idx, answers)}
            onBack={() => setIdx((i) => (i === 0 ? -1 : i - 1))}
          />
        ) : (
          <Intro onStart={start} />
        )}
      </Frame>
    </>
  );
}